import React, { createContext, useContext, useEffect } from 'react'
import {
  RoomProvider,
  useMyPresence,
  layoutRoomId,
  projectRoomId,
  pickPresenceColor,
  isLiveblocksEligible,
  emptyLayoutStorage,
  type Presence,
} from '@/lib/liveblocks'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useAuth } from '@/hooks/useAuth'
import { useMultiplayerLayoutSync } from '@/hooks/useMultiplayerLayoutSync'
import { useMultiplayerAutoSave } from '@/hooks/useMultiplayerAutoSave'
import { useSequencerProjects } from '@/hooks/useSequencerProjects'

/**
 * Synchronous tree-scoped flag that's only `true` when a real
 * `<RoomProvider>` is mounted above the consumer. Presence
 * components (`MaybePresenceDock`, `PresenceCanvasOverlay`) gate
 * their Liveblocks hook calls on this — checking
 * `isLiveblocksEligible(features)` alone isn't enough because the
 * `RoomProvider` only mounts when additional conditions also pass
 * (user loaded, project persisted on server, etc.). Without this
 * flag, any eligibility/provider mismatch surfaces as
 * "RoomProvider is missing from the React tree." React Context is
 * tree-scoped and synchronous, so it can't desync with the
 * provider's actual mount state the way external eligibility
 * checks can.
 */
export const RoomActiveContext = createContext(false)

/** True when a RoomProvider is mounted in the current subtree. */
export function useIsRoomActive(): boolean {
  return useContext(RoomActiveContext)
}

/**
 * Wrappers that gate `RoomProvider` on two conditions:
 *   - The user's effective features include `teamSharing`
 *     (i.e. they're on a paid tier).
 *   - Liveblocks is configured server-side — an empty or missing
 *     secret makes the auth endpoint return 503 and the provider
 *     sits in its initial state forever. The UI degrades silently
 *     in that case: no cursors, no dock.
 *
 * Children always render regardless. When the room isn't mounted,
 * calls to `useMyPresence` / `useOthers` from deeper in the tree
 * would throw, so those hooks live in the presence UI components
 * that are *only* rendered inside the provider branch.
 */

const EMPTY_PRESENCE: Presence = {
  cursor: null,
  focusedDeviceId: null,
  focusedPadIndex: null,
  userName: '',
  userColor: '#888',
}

function initialPresence(name: string, userId: string): Presence {
  return {
    ...EMPTY_PRESENCE,
    userName: name,
    userColor: pickPresenceColor(userId),
  }
}

/**
 * Keeps the peer-visible `userName` / `userColor` in sync with the
 * authenticated user once auth resolves. Mounted inside the
 * provider so it can call `useMyPresence`.
 */
const PresenceIdentitySync: React.FC<{
  userId: string
  userName: string
}> = ({ userId, userName }) => {
  const [, setPresence] = useMyPresence()
  useEffect(() => {
    setPresence({
      userName,
      userColor: pickPresenceColor(userId),
    })
  }, [setPresence, userId, userName])
  return null
}

/**
 * Mounts the CRDT bridge + leader-elected autosave for a layout
 * room. Split into its own component so the hooks run inside the
 * `<RoomProvider>` context — calling `useStorage` / `useMutation`
 * above it would throw.
 */
const LayoutRoomSync: React.FC<{ layoutId: string }> = ({ layoutId }) => {
  useMultiplayerLayoutSync()
  useMultiplayerAutoSave(layoutId)
  return null
}

interface LayoutRoomProps {
  layoutId: string
  children: React.ReactNode
}

export const LayoutRoom: React.FC<LayoutRoomProps> = ({
  layoutId,
  children,
}) => {
  const { user } = useAuth()
  const { features } = useEffectiveFeatures()

  if (!user || !isLiveblocksEligible(features)) {
    return <>{children}</>
  }

  const userId = user.id ?? ''
  if (!userId) return <>{children}</>
  const name = user.email || user.name || `User ${userId.slice(0, 6)}`
  return (
    <RoomProvider
      id={layoutRoomId(layoutId)}
      initialPresence={initialPresence(name, userId)}
      initialStorage={emptyLayoutStorage()}
    >
      <RoomActiveContext.Provider value={true}>
        <PresenceIdentitySync userId={userId} userName={name} />
        <LayoutRoomSync layoutId={layoutId} />
        {children}
      </RoomActiveContext.Provider>
    </RoomProvider>
  )
}

interface ProjectRoomProps {
  projectId: string
  children: React.ReactNode
}

export const ProjectRoom: React.FC<ProjectRoomProps> = ({
  projectId,
  children,
}) => {
  const { user } = useAuth()
  const { features } = useEffectiveFeatures()
  // The Liveblocks auth route's `assertProjectAccess` returns 404
  // for any project id the backend doesn't know about. The
  // sequencer seeds a local-only `'default'` project (and free-tier
  // users never persist their projects at all), so mounting
  // RoomProvider for those would surface as a noisy
  // "Authentication failed: Project not found" in the console with
  // no user-visible recovery path. Gate on the server's project
  // list — if this id isn't in it, the room is single-player only.
  const eligible = !!user && isLiveblocksEligible(features)
  const projectsQuery = useSequencerProjects(eligible)
  const isPersisted = !!projectsQuery.data?.some(p => p.id === projectId)

  if (!eligible || !isPersisted) {
    return <>{children}</>
  }

  const userId = user.id ?? ''
  if (!userId) return <>{children}</>
  const name = user.email || user.name || `User ${userId.slice(0, 6)}`
  return (
    <RoomProvider
      id={projectRoomId(projectId)}
      initialPresence={initialPresence(name, userId)}
      /* Sequencer hasn't migrated to Storage yet — it still
         syncs through REST. The shared Storage shape is
         initialised empty so the room is usable for presence
         and ready for the sequencer Storage migration later. */
      initialStorage={emptyLayoutStorage()}
    >
      <RoomActiveContext.Provider value={true}>
        <PresenceIdentitySync userId={userId} userName={name} />
        {children}
      </RoomActiveContext.Provider>
    </RoomProvider>
  )
}
